// src/adminDashboard/shared/components/KeyboardShortcutsHelp.tsx
// Modal showing all available keyboard shortcuts

import { X, Command } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ShortcutItem {
    keys: string[];
    description: string;
}

interface ShortcutCategory {
    category: string;
    items: ShortcutItem[];
}

const shortcuts: ShortcutCategory[] = [
    {
        category: 'Global',
        items: [
            { keys: ['Ctrl', 'K'], description: 'Open command palette' },
            { keys: ['Ctrl', '/'], description: 'Show keyboard shortcuts' },
            { keys: ['Ctrl', 'B'], description: 'Toggle sidebar' },
            { keys: ['Esc'], description: 'Close modal/dialog' },
        ]
    },
    {
        category: 'Navigation',
        items: [
            { keys: ['G', 'D'], description: 'Go to Dashboard' },
            { keys: ['G', 'U'], description: 'Go to Users' },
            { keys: ['G', 'G'], description: 'Go to Games' },
            { keys: ['G', 'P'], description: 'Go to Pitches' },
            { keys: ['G', 'T'], description: 'Go to Transactions' },
            { keys: ['G', 'L'], description: 'Go to Leagues' },
            { keys: ['G', 'B'], description: 'Go to Blogs' },
        ]
    },
    {
        category: 'List Pages',
        items: [
            { keys: ['N'], description: 'Create new item' },
            { keys: ['R'], description: 'Refresh data' },
            { keys: ['F'], description: 'Focus search' },
            { keys: ['/'], description: 'Focus search (alternative)' },
            { keys: ['Ctrl', 'A'], description: 'Select all items' },
            { keys: ['Ctrl', 'D'], description: 'Deselect all items' },
            { keys: ['E'], description: 'Edit selected item' },
            { keys: ['Delete'], description: 'Delete selected items' },
        ]
    },
];

interface KeyboardShortcutsHelpProps {
    isOpen: boolean;
    onClose: () => void;
}

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;
    if (typeof document === 'undefined') return null;

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-[120] backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                <div
                    className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Command size={20} className="text-primary" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">
                                    Keyboard Shortcuts
                                </h2>
                                <p className="text-sm text-slate-500">
                                    Navigate faster with keyboard shortcuts
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-slate-400" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(80vh-88px)]">
                        <div className="space-y-6">
                            {shortcuts.map((section) => (
                                <div key={section.category}>
                                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                                        {section.category}
                                    </h3>
                                    <div className="space-y-2">
                                        {section.items.map((shortcut, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                                            >
                                                <span className="text-sm text-slate-600">
                                                    {shortcut.description}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    {shortcut.keys.map((key, keyIndex) => (
                                                        <React.Fragment key={keyIndex}>
                                                            <kbd className="px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-sm">
                                                                {key}
                                                            </kbd>
                                                            {keyIndex < shortcut.keys.length - 1 && (
                                                                <span className="text-slate-400 text-xs mx-1">
                                                                    {shortcut.keys.length === 2 && shortcut.keys[0] === 'G' ? 'then' : '+'}
                                                                </span>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer tip */}
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-900">
                                <span className="font-medium">💡 Pro tip:</span> Press{' '}
                                <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-white border border-blue-200 rounded">
                                    Ctrl
                                </kbd>
                                {' + '}
                                <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-white border border-blue-200 rounded">
                                    /
                                </kbd>
                                {' '}anytime to see this help menu
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default KeyboardShortcutsHelp;
